import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ModuleStaffTab from "@/components/staff/ModuleStaffTab";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Search, Settings, FileText, Trash2, Edit3, CheckCircle2, Download,
  Printer, Mail, Eye, ShoppingCart, Repeat, Building2, TrendingUp, Archive,
  RotateCcw, Send, Calendar, FilePenLine, FileWarning, AlertCircle, XCircle, Package, Users,
} from "lucide-react";
import { toast } from "sonner";
import { ModuleSettingsButton } from "@/components/admin/FactoryResetDialog";
import {
  downloadInvoicePDF, printInvoicePDF, downloadInvoiceTXT, generateBarcodeData,
} from "@/lib/invoicePdf";
import InvoicePreview from "@/components/invoicing/InvoicePreview";
import BarcodeScanner from "@/components/invoicing/BarcodeScanner";
import CashFlowPanel from "@/components/invoicing/CashFlowPanel";
import PurchaseInvoicesPanel from "@/components/invoicing/PurchaseInvoicesPanel";
import QuotesPanel from "@/components/invoicing/QuotesPanel";
import RecurringInvoicesPanel from "@/components/invoicing/RecurringInvoicesPanel";
import CustomerRegistryPanel from "@/components/invoicing/CustomerRegistryPanel";

const formatEur = (n) =>
  new Intl.NumberFormat("fi-FI", { style: "currency", currency: "EUR" }).format(Number(n || 0));

// 1:1 Apps2Go status mapping
const STATUS = {
  draft: { label: "Luonnos", icon: FilePenLine, cls: "bg-zinc-700/40 text-zinc-200 border-zinc-500/30" },
  sent: { label: "Lähetetty", icon: Send, cls: "bg-blue-600/20 text-blue-200 border-blue-500/30" },
  paid: { label: "Maksettu", icon: CheckCircle2, cls: "bg-emerald-600/20 text-emerald-200 border-emerald-500/30" },
  overdue: { label: "Erääntynyt", icon: AlertCircle, cls: "bg-orange-600/20 text-orange-200 border-orange-500/30" },
  reminder: { label: "Muistutus", icon: FileWarning, cls: "bg-amber-500/20 text-amber-200 border-amber-500/30" },
  collection: { label: "Perinnässä", icon: AlertCircle, cls: "bg-red-600/30 text-red-200 border-red-500/30" },
  credited: { label: "Hyvitetty", icon: RotateCcw, cls: "bg-purple-600/20 text-purple-200 border-purple-500/30" },
  cancelled: { label: "Peruutettu", icon: XCircle, cls: "bg-zinc-600/30 text-zinc-300 border-zinc-500/30" },
};

const STATUS_FILTERS = ["all", "draft", "sent", "paid", "overdue", "reminder", "collection", "credited", "cancelled"];

export default function InvoicingDashboard() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [tab, setTab] = useState("sales");
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [emailDialog, setEmailDialog] = useState(null);
  const [emailTo, setEmailTo] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [inv, ps, st] = await Promise.all([
        api.get("/invoices"),
        api.get("/purchase-invoices"),
        api.get("/settings/invoice"),
      ]);
      setInvoices(inv.data);
      setPurchases(ps.data);
      setSettings(st.data || {});
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  // ============================ Derived ============================
  const filtered = useMemo(() => {
    let list = invoices;
    if (statusFilter !== "all") list = list.filter((i) => i.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (i) =>
          (i.invoice_number || "").toLowerCase().includes(q) ||
          (i.recipient_name || "").toLowerCase().includes(q) ||
          (i.reference_number || "").includes(q),
      );
    }
    return list;
  }, [invoices, statusFilter, search]);

  const stats = useMemo(() => {
    const today = new Date().toISOString().split("T")[0];
    return {
      total: invoices.length,
      open: invoices.filter((i) => ["sent", "overdue", "reminder"].includes(i.status)).reduce((s, i) => s + (Number(i.total_amount ?? i.total) || 0), 0),
      paid: invoices.filter((i) => i.status === "paid").reduce((s, i) => s + (Number(i.total_amount ?? i.total) || 0), 0),
      overdue: invoices.filter((i) => i.due_date && i.due_date < today && !["paid", "cancelled", "credited"].includes(i.status)).length,
    };
  }, [invoices]);

  const cancelled = invoices.filter((i) => i.status === "cancelled");

  // ============================ Actions ============================
  const refreshStatus = async (id, status) => {
    try {
      await api.patch(`/invoices/${id}/status`, null, { params: { status } });
      toast.success("Tila päivitetty");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const markPaid = async (id) => {
    try {
      const r = await api.post(`/invoices/${id}/mark-paid`);
      const posted = r.data.accounting_entries_posted || 0;
      toast.success(`Merkitty maksetuksi. Kirjanpitoon vietiin ${posted} kirjausta`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Poistetaanko lasku pysyvästi?")) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Poistettu");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const createCredit = async (id) => {
    if (!window.confirm("Luodaanko hyvityslasku tästä laskusta?")) return;
    try {
      const r = await api.post(`/invoices/${id}/create-credit`);
      toast.success(`Hyvityslasku #${r.data.invoice_number} luotu`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const bulkMarkPaid = async () => {
    if (selected.size === 0) return;
    try {
      const r = await api.post("/invoices/bulk-mark-paid", { ids: [...selected] });
      toast.success(`${r.data.marked_paid} laskua merkittiin maksetuksi`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const bulkCancel = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Peruutetaanko ${selected.size} laskua?`)) return;
    try {
      await api.post("/invoices/bulk-status", { ids: [...selected] }, { params: { status: "cancelled" } });
      toast.success("Peruutettu");
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendReminders = async () => {
    try {
      const r = await api.post("/invoices/send-reminders");
      toast.success(`${r.data.reminders_sent} muistutusta lähetetty`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const syncAccounting = async () => {
    try {
      const r = await api.post("/invoices/sync-accounting");
      toast.success(`Kirjanpitoon vietiin ${r.data.accounting_entries_posted} kirjausta`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendEmail = (inv) => {
    setEmailDialog(inv);
    setEmailTo(inv.recipient_email || "");
  };

  const submitEmail = () => {
    if (!emailDialog || !emailTo) return;
    const subj = encodeURIComponent(`Lasku #${emailDialog.invoice_number}`);
    const body = encodeURIComponent(
      `Hyvä ${emailDialog.recipient_name},\n\nLähetämme oheisen laskun #${emailDialog.invoice_number}.\n\nSumma: ${(Number(emailDialog.total_amount ?? emailDialog.total) || 0).toFixed(2)} €\nEräpäivä: ${emailDialog.due_date}\nViitenumero: ${emailDialog.reference_number}\nTilinumero: ${settings.bank_account || ""}\n\nYstävällisin terveisin,\n${settings.company_name || ""}`,
    );
    window.location.href = `mailto:${emailTo}?subject=${subj}&body=${body}`;
    setEmailDialog(null);
    refreshStatus(emailDialog.id, "sent");
  };

  const onPdf = (inv) => {
    try {
      downloadInvoicePDF(inv, settings);
      toast.success(`PDF luotu (lasku #${inv.invoice_number}) — tarkista uusi välilehti / latauksesi`);
    } catch (e) {
      toast.error("PDF:n luonti epäonnistui: " + (e?.message || "tuntematon virhe"));
    }
  };
  const onPrint = (inv) => {
    try {
      printInvoicePDF(inv, settings);
      toast.success("Tulostusikkuna avautuu...");
    } catch (e) {
      toast.error("Tulostuksen avaaminen epäonnistui: " + (e?.message || "tuntematon virhe"));
    }
  };
  const onTxt = (inv) => downloadInvoiceTXT(inv, settings);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((i) => i.id)));
  };

  // Group by year-month for Arkisto
  const archive = useMemo(() => {
    const m = {};
    invoices.forEach((i) => {
      const key = (i.invoice_date || "").slice(0, 7) || "muut";
      if (!m[key]) m[key] = [];
      m[key].push(i);
    });
    return Object.entries(m).sort(([a], [b]) => b.localeCompare(a));
  }, [invoices]);

  // ============================ Render ============================
  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6" data-testid="invoicing-dashboard">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-display font-bold text-amber-100">Laskutus</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Myyntilaskut, ostolaskut, tarjoukset, toistuvat – kaikki yhdessä paikassa.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <BarcodeScanner
            onScanned={(d) => {
              toast.info(`Viitenumero ${d.reference} · ${d.amount.toFixed(2)} € · ${d.iban}`);
            }}
          />
          <Button variant="outline" onClick={() => navigate("/invoicing/items")} className="gap-2" data-testid="invoicing-items-btn">
            <Package className="w-4 h-4" /> Nimikkeet
          </Button>
          <ModuleSettingsButton moduleKey="invoicing" moduleLabel="Laskutus" onAfterReset={() => window.location.reload()} />
          <Button variant="outline" onClick={() => navigate("/invoicing/settings")} className="gap-2" data-testid="invoicing-settings-btn">
            <Settings className="w-4 h-4" /> Asetukset
          </Button>
          <Button
            className="gap-2 bg-amber-500 text-zinc-900 hover:bg-amber-400 font-medium"
            onClick={() => navigate("/invoicing/new")}
            data-testid="invoicing-new-btn"
          >
            <Plus className="w-4 h-4" /> Uusi lasku
          </Button>
        </div>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Yhteensä" value={stats.total} />
        <KPI label="Avoinna" value={formatEur(stats.open)} accent="amber" />
        <KPI label="Maksetut" value={formatEur(stats.paid)} accent="emerald" />
        <KPI label="Erääntyneet (kpl)" value={stats.overdue} accent="red" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap gap-1 bg-zinc-900/60 border border-amber-500/20 p-1 rounded-xl">
          <TabsTrigger value="sales" className="gap-2 data-[state=active]:bg-amber-500 data-[state=active]:text-zinc-900" data-testid="tab-sales">
            <FileText className="w-4 h-4" /> Myyntilaskut
          </TabsTrigger>
          <TabsTrigger value="purchases" className="gap-2 data-[state=active]:bg-amber-500 data-[state=active]:text-zinc-900" data-testid="tab-purchases">
            <ShoppingCart className="w-4 h-4" /> Ostolaskut
          </TabsTrigger>
          <TabsTrigger value="quotes" className="gap-2 data-[state=active]:bg-amber-500 data-[state=active]:text-zinc-900" data-testid="tab-quotes">
            <FilePenLine className="w-4 h-4" /> Tarjoukset
          </TabsTrigger>
          <TabsTrigger value="recurring" className="gap-2 data-[state=active]:bg-amber-500 data-[state=active]:text-zinc-900" data-testid="tab-recurring">
            <Repeat className="w-4 h-4" /> Toistuvat
          </TabsTrigger>
          <TabsTrigger value="customers" className="gap-2 data-[state=active]:bg-amber-500 data-[state=active]:text-zinc-900" data-testid="tab-customers">
            <Building2 className="w-4 h-4" /> Asiakkaat
          </TabsTrigger>
          <TabsTrigger value="cashflow" className="gap-2 data-[state=active]:bg-amber-500 data-[state=active]:text-zinc-900" data-testid="tab-cashflow">
            <TrendingUp className="w-4 h-4" /> Kassavirta
          </TabsTrigger>
          <TabsTrigger value="archive" className="gap-2 data-[state=active]:bg-amber-500 data-[state=active]:text-zinc-900" data-testid="tab-archive">
            <Archive className="w-4 h-4" /> Arkisto
          </TabsTrigger>
          <TabsTrigger value="cancelled" className="gap-2 data-[state=active]:bg-amber-500 data-[state=active]:text-zinc-900" data-testid="tab-cancelled">
            <XCircle className="w-4 h-4" /> Peruutetut
            {cancelled.length > 0 && <Badge variant="outline" className="ml-1">{cancelled.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="staff" className="gap-2 data-[state=active]:bg-amber-500 data-[state=active]:text-zinc-900" data-testid="tab-staff">
            <Users className="w-4 h-4" /> Henkilökunta
          </TabsTrigger>
        </TabsList>

        {/* SALES */}
        <TabsContent value="sales" className="mt-4 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="relative w-full md:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Hae #, asiakas, viite..."
                className="pl-9"
                data-testid="invoicing-search"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-44" data-testid="invoicing-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_FILTERS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s === "all" ? "Kaikki tilat" : STATUS[s]?.label || s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={sendReminders} className="gap-2" data-testid="invoicing-send-reminders">
                <FileWarning className="w-4 h-4" /> Lähetä muistutukset
              </Button>
              <Button variant="outline" onClick={syncAccounting} className="gap-2" data-testid="invoicing-sync-accounting">
                <RotateCcw className="w-4 h-4" /> Synkkaa kirjanpitoon
              </Button>
            </div>
          </div>

          {selected.size > 0 && (
            <div className="flex items-center justify-between gap-3 px-4 py-2 rounded-xl bg-amber-500/10 border border-amber-500/30">
              <p className="text-sm">
                <strong>{selected.size}</strong> valittu
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={bulkMarkPaid} className="gap-1" data-testid="invoicing-bulk-paid">
                  <CheckCircle2 className="w-3 h-3" /> Merkitse maksetuksi
                </Button>
                <Button size="sm" variant="outline" onClick={bulkCancel} className="gap-1" data-testid="invoicing-bulk-cancel">
                  <XCircle className="w-3 h-3" /> Peruuta
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
                  Tyhjennä
                </Button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center py-12 text-muted-foreground">Ladataan...</div>
          ) : filtered.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-20" />
                <p>Ei laskuja. Luo ensimmäinen lasku!</p>
                <Button className="mt-4 bg-amber-500 hover:bg-amber-400 text-zinc-900" onClick={() => navigate("/invoicing/new")}>
                  <Plus className="w-4 h-4 mr-1" /> Uusi lasku
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-amber-500/20">
              <table className="w-full text-sm">
                <thead className="bg-zinc-900/60">
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-amber-500/20">
                    <th className="py-3 px-3 w-8">
                      <Checkbox
                        checked={selected.size === filtered.length && filtered.length > 0}
                        onCheckedChange={toggleSelectAll}
                        data-testid="invoicing-select-all"
                      />
                    </th>
                    <th className="py-3 px-3">#</th>
                    <th className="py-3 px-3">Asiakas</th>
                    <th className="py-3 px-3">Päivä</th>
                    <th className="py-3 px-3">Eräpäivä</th>
                    <th className="py-3 px-3 text-right">Summa</th>
                    <th className="py-3 px-3">Tila</th>
                    <th className="py-3 px-3 w-64"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((i) => {
                    const st = STATUS[i.status] || STATUS.draft;
                    const Icon = st.icon;
                    return (
                      <tr key={i.id} className="border-b border-amber-500/10 hover:bg-amber-500/5 transition" data-testid={`invoice-row-${i.id}`}>
                        <td className="py-3 px-3">
                          <Checkbox
                            checked={selected.has(i.id)}
                            onCheckedChange={() => toggleSelect(i.id)}
                            data-testid={`invoice-select-${i.id}`}
                          />
                        </td>
                        <td className="py-3 px-3 font-mono">{i.invoice_number}</td>
                        <td className="py-3 px-3">{i.recipient_name}</td>
                        <td className="py-3 px-3 text-xs text-muted-foreground">{i.invoice_date}</td>
                        <td className="py-3 px-3 text-xs text-muted-foreground">{i.due_date}</td>
                        <td className="py-3 px-3 text-right font-semibold">{formatEur(i.total_amount ?? i.total)}</td>
                        <td className="py-3 px-3">
                          <Badge className={`text-[10px] gap-1 ${st.cls}`}>
                            <Icon className="w-3 h-3" /> {st.label}
                          </Badge>
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex gap-1 justify-end flex-wrap">
                            <ActionBtn title="Esikatsele" onClick={() => setPreviewInvoice(i)} icon={Eye} testid={`invoice-preview-${i.id}`} />
                            <ActionBtn title="Lataa PDF" onClick={() => onPdf(i)} icon={Download} testid={`invoice-pdf-${i.id}`} />
                            <ActionBtn title="Tulosta" onClick={() => onPrint(i)} icon={Printer} testid={`invoice-print-${i.id}`} />
                            <ActionBtn title="Lähetä sähköpostiin" onClick={() => sendEmail(i)} icon={Mail} testid={`invoice-email-${i.id}`} />
                            {i.status !== "paid" && i.status !== "cancelled" && (
                              <ActionBtn title="Merkitse maksetuksi" onClick={() => markPaid(i.id)} icon={CheckCircle2} testid={`invoice-mark-paid-${i.id}`} className="text-emerald-400" />
                            )}
                            {i.status !== "credited" && i.status !== "cancelled" && (
                              <ActionBtn title="Hyvityslasku" onClick={() => createCredit(i.id)} icon={RotateCcw} testid={`invoice-credit-${i.id}`} className="text-purple-300" />
                            )}
                            <ActionBtn title="Muokkaa" onClick={() => navigate(`/invoicing/${i.id}`)} icon={Edit3} testid={`invoice-edit-${i.id}`} />
                            <ActionBtn title="Poista" onClick={() => remove(i.id)} icon={Trash2} testid={`invoice-delete-${i.id}`} className="text-destructive" />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="purchases" className="mt-4">
          <PurchaseInvoicesPanel onItems={setPurchases} />
        </TabsContent>
        <TabsContent value="quotes" className="mt-4">
          <QuotesPanel settings={settings} onConvertToInvoice={load} />
        </TabsContent>
        <TabsContent value="recurring" className="mt-4">
          <RecurringInvoicesPanel settings={settings} onInvoiceCreated={load} />
        </TabsContent>
        <TabsContent value="customers" className="mt-4">
          <CustomerRegistryPanel />
        </TabsContent>
        <TabsContent value="cashflow" className="mt-4">
          <CashFlowPanel invoices={invoices} purchases={purchases} />
        </TabsContent>

        <TabsContent value="archive" className="mt-4 space-y-3">
          {archive.length === 0 && <p className="text-muted-foreground text-sm">Ei arkistoitavaa.</p>}
          {archive.map(([month, list]) => (
            <Card key={month}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-amber-400" />
                    <h3 className="font-semibold">{month === "muut" ? "Muut" : month}</h3>
                    <Badge variant="outline">{list.length} laskua</Badge>
                  </div>
                  <span className="text-sm font-semibold text-emerald-300">
                    {formatEur(list.reduce((s, i) => s + (Number(i.total_amount ?? i.total) || 0), 0))}
                  </span>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {list.map((i) => (
                    <button
                      key={i.id}
                      onClick={() => navigate(`/invoicing/${i.id}`)}
                      className="text-left p-2 rounded-lg border border-amber-500/10 hover:border-amber-500/40 transition bg-zinc-900/40"
                    >
                      <p className="text-sm font-mono">#{i.invoice_number}</p>
                      <p className="text-xs text-muted-foreground">{i.recipient_name}</p>
                      <p className="text-xs font-semibold mt-1">{formatEur(i.total_amount ?? i.total)}</p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="cancelled" className="mt-4 space-y-2">
          {cancelled.length === 0 ? (
            <p className="text-muted-foreground text-sm py-8 text-center">Ei peruutettuja laskuja.</p>
          ) : (
            cancelled.map((i) => (
              <Card key={i.id}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-mono text-sm">#{i.invoice_number}</p>
                    <p className="text-xs text-muted-foreground">{i.recipient_name} · {i.invoice_date}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold">{formatEur(i.total_amount ?? i.total)}</span>
                    <Button size="sm" variant="outline" onClick={() => refreshStatus(i.id, "draft")} className="gap-1">
                      <RotateCcw className="w-3 h-3" /> Palauta luonnokseksi
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* HENKILÖKUNTA (Phase 17B) */}
        <TabsContent value="staff" className="mt-4">
          <ModuleStaffTab moduleKey="invoicing" moduleLabel="Laskutus" />
        </TabsContent>
      </Tabs>

      {/* Preview dialog */}
      <Dialog open={!!previewInvoice} onOpenChange={(v) => !v && setPreviewInvoice(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0 bg-white" data-testid="invoice-preview-dialog">
          <DialogHeader className="px-4 pt-4">
            <DialogTitle className="text-black">Laskun esikatselu</DialogTitle>
          </DialogHeader>
          {previewInvoice && (
            <InvoicePreview
              form={previewInvoice}
              subtotal={Number(previewInvoice.subtotal) || 0}
              vatTotal={Number(previewInvoice.vat_total) || 0}
              totalAmount={Number(previewInvoice.total_amount ?? previewInvoice.total) || 0}
              barcodeData={
                previewInvoice.barcode_data ||
                generateBarcodeData(
                  previewInvoice.sender_bank_account || settings.bank_account,
                  previewInvoice.total_amount ?? previewInvoice.total,
                  previewInvoice.reference_number,
                  previewInvoice.due_date,
                )
              }
              settings={settings}
            />
          )}
          <DialogFooter className="px-4 pb-4 gap-2 bg-zinc-900 rounded-b-lg">
            <Button variant="outline" onClick={() => onPdf(previewInvoice)} className="gap-2">
              <Download className="w-4 h-4" /> PDF
            </Button>
            <Button variant="outline" onClick={() => onPrint(previewInvoice)} className="gap-2">
              <Printer className="w-4 h-4" /> Tulosta
            </Button>
            <Button variant="outline" onClick={() => onTxt(previewInvoice)} className="gap-2">
              TXT
            </Button>
            <Button onClick={() => setPreviewInvoice(null)} className="bg-amber-500 text-zinc-900 hover:bg-amber-400">
              Sulje
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email dialog */}
      <Dialog open={!!emailDialog} onOpenChange={(v) => !v && setEmailDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Lähetä lasku sähköpostiin</DialogTitle>
          </DialogHeader>
          {emailDialog && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Lasku #{emailDialog.invoice_number} — {formatEur(emailDialog.total_amount ?? emailDialog.total)}
              </p>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Vastaanottajan sähköposti</label>
                <Input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="asiakas@email.fi" data-testid="email-to" />
              </div>
              <p className="text-xs text-muted-foreground">
                Avaa sähköpostiohjelmasi esitäytetyllä viestillä. Voit liittää PDF-tiedoston ennen lähetystä.
              </p>
              <div className="flex gap-2">
                <Button onClick={submitEmail} disabled={!emailTo} className="flex-1 gap-2 bg-amber-500 hover:bg-amber-400 text-zinc-900" data-testid="email-send">
                  <Mail className="w-4 h-4" /> Avaa sähköpostissa
                </Button>
                <Button variant="outline" onClick={() => setEmailDialog(null)}>
                  Peruuta
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ label, value, accent = "amber" }) {
  const colour = {
    amber: "text-amber-300",
    emerald: "text-emerald-300",
    red: "text-red-300",
  }[accent];
  return (
    <Card className="bg-zinc-900/60 border-amber-500/20">
      <CardContent className="p-4">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className={`text-xl md:text-2xl font-bold mt-1 ${colour}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

function ActionBtn({ icon: Icon, onClick, title, testid, className = "" }) {
  return (
    <Button
      size="icon"
      variant="ghost"
      title={title}
      onClick={onClick}
      data-testid={testid}
      className={`w-9 h-9 hover:bg-amber-500/10 hover:text-amber-200 ${className}`}
    >
      <Icon className="w-4 h-4" />
    </Button>
  );
}
